import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { errorMsg, postJson } from "../api";

/**
 * ? SAFE-PCB rules (from docs)
 * - Min Circuit X/Y (we enforce on size_x/size_y):
 *   RIGID/HIGH_FREQ/RIGID_FLEX/NO_COPPER=3mm, METAL=4mm, FLEX=6mm
 */
const SAFE_RULES = {
  sizeMinByFamily: {
    RIGID: 3,
    HIGH_FREQ: 3,
    RIGID_FLEX: 3,
    NO_COPPER: 3,
    METAL: 4,
    FLEX: 6,
  },
  allowedSectionsByFamily: {
    RIGID: new Set(["BASE", "PANEL", "RIGID", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
    HIGH_FREQ: new Set(["BASE", "PANEL", "RIGID", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
    METAL: new Set(["BASE", "PANEL", "RIGID", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
    NO_COPPER: new Set(["BASE", "PANEL", "RIGID", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
    FLEX: new Set(["BASE", "PANEL", "FLEX", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
    RIGID_FLEX: new Set(["BASE", "PANEL", "RIGID", "FLEX", "SOLDERMASK", "MARKING", "OPTIONS", "STACKUP", "PEELABLE"]),
  },
};
// ----------------------------
function normStr(v) {
  return (v ?? "").toString().trim();
}
function hasMeaningfulValue(v) {
  return normStr(v) !== "";
}
function isNA(v) {
  const s = normStr(v).toUpperCase();
  return s === "NA" || s === "N/A";
}
function isWITHOUT(v) {
  return normStr(v).toUpperCase() === "WITHOUT";
}
function toNum(v) {
  const s = normStr(v).replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
function clampNumber(n, { min, max }) {
  if (n === null) return null;
  let x = n;
  if (min !== undefined && min !== null) x = Math.max(x, min);
  if (max !== undefined && max !== null) x = Math.min(x, max);
  if (!Number.isFinite(x)) return null;
  return x;
}

function getFamily(selected) {
  return (selected?.family || "").toString().trim().toUpperCase();
}
function getSizeMin(selected) {
  const fam = getFamily(selected);
  return SAFE_RULES.sizeMinByFamily[fam] ?? 1;
}
function isFieldAllowedByFamily(def, selected) {
  const fam = getFamily(selected);
  const sec = (def?.section || def?.SECTION || "").toString().trim().toUpperCase();
  const allowed = SAFE_RULES.allowedSectionsByFamily[fam];
  if (!allowed) return true;
  if (!sec) return true;
  return allowed.has(sec);
}

function apiLabelFromDef(def, fallbackKey) {
  const apiName = def?.api_name || fallbackKey;
  return (apiName || fallbackKey || "").replaceAll("_", " ");
}
// (e.g. "MUL_35um") while meta.api retains the original API value ("35um") — they diverge.
function toOptValue(o) {
  return o?.value ?? o?.meta?.api ?? o?.api ?? o?.API ?? o?.id ?? o?.ID ?? "";
}
// ? LABEL = humain
function toOptLabel(o) {
  return o?.label ?? "";
}

function makeLabelsUnique(options) {
  const counts = new Map();
  (options || []).forEach((o) => {
    const label = String(o?.label ?? "");
    if (!label) return;
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return (options || []).map((o) => {
    const l = String(o?.label ?? "");
    if (!l) return o;
    if ((counts.get(l) || 0) <= 1) return o;
    return o; // keep original label even if duplicate — value is the unique key
  });
}


const NUMERIC_OPTION_KEYS = new Set([
  "layers",
  "thickness",
  "copper_external",
  "external_copper_thickness",
  "copper_internal",
  "inner_copper_thickness",
  "gold_finger_thickness",
  "stiffener_thickness",
  "coverlay_thickness",
]);

const THICKNESS_MM_KEYS = new Set(["thickness"]);
const COPPER_UM_KEYS = new Set([
  "copper_external",
  "external_copper_thickness",
  "copper_internal",
  "inner_copper_thickness",
  "gold_finger_thickness",
  "stiffener_thickness",
  "coverlay_thickness",
]);

function extractOptionSortNumber(key, option) {
  if (!NUMERIC_OPTION_KEYS.has(key)) return null;
  const raw = [option?.label, option?.value].find((x) => normStr(x));
  const match = String(raw || "").replace(",", ".").match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const n = Number(match[0]);
  return Number.isFinite(n) ? n : null;
}

function formatNumericOptionLabel(key, numericValue, rawLabel = "") {
  const suffix = String(rawLabel || "").toLowerCase().includes("default choice") ? " (default choice)" : "";
  if (key === "layers") return `${numericValue}${suffix}`;
  if (THICKNESS_MM_KEYS.has(key)) return `${numericValue} mm${suffix}`;
  if (COPPER_UM_KEYS.has(key)) return `${numericValue} µm${suffix}`;
  return `${numericValue}${suffix}`;
}

function getFieldVariants(key) {
  return SHOWN_FIELDS_ORDER.find((variants) => variants.includes(key)) || [key];
}

function findDefaultOpt(opts) {
  return (
    opts.find((o) => String(o?.label ?? "").toLowerCase().includes("default choice")) ||
    opts.find((o) => !String(toOptValue(o) ?? "").toUpperCase().includes("SPECIAL")) ||
    opts[0] ||
    null
  );
}

function sanitizeSelectedState(rawSelected) {
  const src = rawSelected && typeof rawSelected === "object" ? rawSelected : {};
  const out = {};

  SHOWN_FIELDS_ORDER.forEach((variants) => {
    const canonicalKey = variants[0];
    const foundKey = variants.find((variant) => Object.prototype.hasOwnProperty.call(src, variant));
    if (!foundKey) return;
    const value = src[foundKey];
    out[canonicalKey] = value == null ? "" : String(value);
  });

  ["size_x", "size_y", "pcb_quantity"].forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(src, key)) return;
    out[key] = src[key] == null ? "" : String(src[key]);
  });

  ["quote_mode", "manual_note", "manual_reason"].forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(src, key)) return;
    out[key] = src[key];
  });

  return out;
}

function isFormattingOnly(requested, applied) {
  const r = normStr(requested);
  const a = normStr(applied);
  if (r === a) return true;
  const rn = toNum(r);
  const an = toNum(a);
  if (rn !== null && an !== null && Math.abs(rn - an) < 1e-9) return true;
  return false;
}

function isEmptyOrNA(v) {
  const s = normStr(v);
  if (!s) return true;
  const u = s.toUpperCase();
  return u === "NA" || u === "N/A";
}
// ----------------------------
function Spinner({ size = 14 }) {
  return (
    <span
      aria-label="loading"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2px solid rgba(0,0,0,0.18)",
        borderTopColor: "rgba(0,0,0,0.65)",
        display: "inline-block",
        animation: "spin 0.9s linear infinite",
      }}
    />
  );
}

function FieldRow({ label, children, missing, filtered, added, removed, changedCount, overridden, pending }) {
  const baseBorder = "1px solid rgba(0,0,0,0.10)";
  let borderColor = baseBorder;
  let bg = "transparent";

  if (missing) {
    borderColor = "2px solid #d64545";
    bg = "rgba(214, 69, 69, 0.08)";
  } else if (pending) {
    borderColor = "2px solid #d64545";
    bg = "rgba(214, 69, 69, 0.06)";
  } else if (overridden) {
    borderColor = "2px solid #7a4bd1";
    bg = "rgba(122, 75, 209, 0.08)";
  } else if (removed) {
    borderColor = "2px solid #d64545";
    bg = "rgba(214, 69, 69, 0.06)";
  } else if (added) {
    borderColor = "2px solid #2f9e44";
    bg = "rgba(47, 158, 68, 0.06)";
  } else if (filtered) {
    borderColor = "2px solid #f08c00";
    bg = "rgba(240, 140, 0, 0.06)";
  }

  const pill = (text, color, bgc) => (
    <span
      style={{
        fontSize: 12,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
        background: bgc,
        fontWeight: 700,
      }}
    >
      {text}
    </span>
  );

  return (
    <div
      className="form-row"
      style={{
        border: borderColor,
        background: bg,
        borderRadius: 10,
        padding: 10,
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <label className="label" style={{ margin: 0 }}>
          {label}
        </label>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {missing ? pill("MANQUANT", "#d64545", "rgba(214,69,69,0.10)") : null}
          {pending ? pill("ACTION REQUISE", "#d64545", "rgba(214,69,69,0.10)") : null}
          {overridden ? pill("MODIFIÉ", "#7a4bd1", "rgba(122,75,209,0.10)") : null}
          {added ? pill("CHAMP AJOUTÉ", "#2f9e44", "rgba(47,158,68,0.10)") : null}
          {removed ? pill("CHAMP SUPPRIMÉ", "#d64545", "rgba(214,69,69,0.10)") : null}
          {filtered ? pill(`FILTRÉ${changedCount != null ? ` (${changedCount})` : ""}`, "#f08c00", "rgba(240,140,0,0.10)") : null}
        </div>
      </div>

      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

function SelectField({ label, value, mergedOptions, onChange, disabled, missing, metaBadges, pendingBlock }) {
  const v = value ?? "";
  const visibleOptions = (mergedOptions || []).filter((o) => {
    const status = o?.status || "kept";
    if (status !== "removed") return true;
    return String(o?.value ?? "") === v;
  });
  return (
    <FieldRow label={label} missing={missing} {...(metaBadges || {})}>
      <select className="select" value={v} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ width: "100%" }}>
        <option value=""></option>

        {visibleOptions.map((o, idx) => {
            const ov = o?.value ?? "";
            const ol = o?.label ?? ov ?? "";
            const status = o?.status || "kept";
            const suffix = status === "removed" ? "  (non disponible)" : "";
            return (
              <option key={`${ov}-${idx}`} value={ov} disabled={status === "removed"}>
                {ol}
                {suffix}
              </option>
            );
          })}
      </select>

      {pendingBlock ? <div style={{ marginTop: 10 }}>{pendingBlock}</div> : null}
    </FieldRow>
  );
}

function NumericStepper({ label, value, onChange, onCommit, step = 1, bigStep = 10, min, max, unit, disabled, presets = [], missing = false, metaBadges }) {
  const canonical = value === "" || value === null || value === undefined ? "" : String(value);
  const [localVal, setLocalVal] = React.useState(canonical);
  const bumpTimerRef = React.useRef(null);
  const pendingBumpRef = React.useRef(null);
  const onCommitRef = React.useRef(onCommit);
  onCommitRef.current = onCommit;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  React.useEffect(() => {
    if (pendingBumpRef.current === null) setLocalVal(canonical);
  }, [canonical]);

  const bump = (delta) => {
    const base = pendingBumpRef.current !== null
      ? pendingBumpRef.current
      : (localVal === "" ? 0 : Number(String(localVal).replace(",", ".")));
    const next = clampNumber(base + delta, { min, max });
    if (next === null) return;
    const nextStr = String(next);
    pendingBumpRef.current = next;
    setLocalVal(nextStr);
    onChangeRef.current?.(nextStr);      // Parent state update immediately (no fetch, just sync)
    clearTimeout(bumpTimerRef.current);
    bumpTimerRef.current = setTimeout(() => {
      pendingBumpRef.current = null;
      onCommitRef.current?.(nextStr);    // Trigger fetch (only if onCommit is provided)
    }, 800);
  };

  const setPreset = (p) => {
    const next = clampNumber(p, { min, max });
    if (next === null) return;
    const nextStr = String(next);
    clearTimeout(bumpTimerRef.current);
    pendingBumpRef.current = null;
    setLocalVal(nextStr);
    onChange(nextStr);
    onCommitRef.current?.(nextStr);
  };

  const commitLocal = () => {
    clearTimeout(bumpTimerRef.current);
    pendingBumpRef.current = null;
    onChange(localVal);
    onCommitRef.current?.(localVal);
  };

  return (
    <FieldRow label={label} missing={missing} {...(metaBadges || {})}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button type="button" className="btn btn-sm" onClick={() => bump(-bigStep)} disabled={disabled}>
          -{bigStep}
        </button>
        <button type="button" className="btn btn-sm" onClick={() => bump(-step)} disabled={disabled}>
          -
        </button>

        <input
          value={localVal}
          disabled={disabled}
          onChange={(e) => {
            const v = e.target.value;
            pendingBumpRef.current = null;
            setLocalVal(v);
            onChangeRef.current?.(v);    // Sync parent state on every keystroke (no fetch)
          }}
          onBlur={commitLocal}
          onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
          className="input"
          inputMode="decimal"
          style={{ flex: 1 }}
        />

        <button type="button" className="btn btn-sm" onClick={() => bump(step)} disabled={disabled}>
          +
        </button>
        <button type="button" className="btn btn-sm" onClick={() => bump(bigStep)} disabled={disabled}>
          +{bigStep}
        </button>

        {unit ? <span className="unit">{unit}</span> : null}
      </div>

      {presets?.length ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          {presets.map((p) => (
            <button key={String(p)} type="button" className="btn btn-ghost btn-sm" onClick={() => setPreset(p)} disabled={disabled}>
              {p} {unit || ""}
            </button>
          ))}
        </div>
      ) : null}
    </FieldRow>
  );
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: "min(720px, 96vw)",
          borderRadius: 14,
          background: "#fff",
          border: "1px solid rgba(0,0,0,0.12)",
          boxShadow: "0 16px 60px rgba(0,0,0,0.25)",
          padding: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
          <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
          <button type="button" className="btn" onClick={onClose} style={{ background: "#fff", color: "#111", border: "1px solid rgba(0,0,0,0.2)" }}>
             x
          </button>
        </div>
        <div style={{ marginTop: 12 }}>{children}</div>
      </div>
    </div>
  );
}

// Valeurs et labels par défaut affichés avant le premier "Mettre à jour"
const FIELD_DEFAULTS = {
  family:              { value: "RIGID",     label: "Standard rigid PCB (S1000H)" },
  material:            { value: "S1000H",        label: "Material Shengyi S1000H (FR4 TG150)" },
  surface_finish:      { value: "CHEM.GOLD",    label: "Chemical ENIG / Gold finition" },
  finish:              { value: "CHEM.GOLD",    label: "Chemical ENIG / Gold finition" },
  layers:              { value: "4",             label: "4" },
  thickness:           { value: "S1000H_1.6mm", label: "1.6 mm" },
  copper_external:     { value: "MUL_35um",     label: "35 µm" },
  external_copper_thickness: { value: "MUL_35um", label: "35 µm" },
  copper_internal:     { value: "17um",          label: "17.5 µm" },
  inner_copper_thickness:    { value: "17um",    label: "17.5 µm" },
  stackup_method:      { value: "SAFE",      label: "Stackup configuration is Safe-PCB best choice" },
  stackup:             { value: "SAFE",      label: "Stackup configuration is Safe-PCB best choice" },
  stackup_type:        { value: "SAFE",      label: "Stackup configuration is Safe-PCB best choice" },
  silkscreen_color:    { value: "WHITE",     label: "White ink color" },
  silkscreen_ink_color:{ value: "WHITE",     label: "White ink color" },
  silkscreen:          { value: "WHITE",     label: "White ink color" },
  color_top:           { value: "GREEN",     label: "Green soldermask" },
  solder_color_top:    { value: "GREEN",     label: "Green soldermask" },
  color_bottom:        { value: "GREEN",     label: "Green soldermask" },
  color_bot:           { value: "GREEN",     label: "Green soldermask" },
  solder_color_bot:    { value: "GREEN",     label: "Green soldermask" },
  soldermask_position: { value: "TOP.BOT",   label: "Soldermask on both side with same color" },
  position:            { value: "TOP.BOT",   label: "Soldermask on both side with same color" },
  legend:              { value: "WITHOUT",   label: "Without silkscreen / legend" },
  date_position:       { value: "WITHOUT",   label: "Without" },
  pcb_supplier:        { value: "WITHOUT",   label: "Without" },
  supplier:            { value: "WITHOUT",   label: "Without" },
  ipc_class:           { value: "CLASS_2",   label: "IPC 600 - Class 2" },
  ipc:                 { value: "CLASS_2",   label: "IPC 600 - Class 2" },
};

// Labels UI français pour chaque champ (toutes variantes de noms API)
const FIELD_LABELS = {
  family: "Famille PCB",
  material: "Matériau",
  surface_finish: "Finition de surface", finish: "Finition de surface",
  layers: "Nombre de couches",
  thickness: "Épaisseur",
  copper_external: "Cuivre externe", external_copper_thickness: "Cuivre externe",
  copper_internal: "Cuivre interne", inner_copper_thickness: "Cuivre interne",
  stackup_method: "Configuration stackup", stackup: "Configuration stackup", stackup_type: "Configuration stackup",
  silkscreen_color: "Couleur sérigraphie", silkscreen_ink_color: "Couleur sérigraphie", silkscreen: "Couleur sérigraphie",
  color_top: "Couleur TOP", solder_color_top: "Couleur TOP",
  color_bottom: "Couleur BOTTOM", color_bot: "Couleur BOTTOM", solder_color_bot: "Couleur BOTTOM",
  soldermask_position: "Position masque", position: "Position masque",
  legend: "Sérigraphie",
  date_position: "Date code",
  pcb_supplier: "Fournisseur PCB", supplier: "Fournisseur PCB",
  ipc_class: "Classe IPC", ipc: "Classe IPC",
};
// Chaque entrée liste les variantes possibles du nom renvoyé par l'API.
const SHOWN_FIELDS_ORDER = [
  ["family"],
  ["material"],
  ["surface_finish", "finish"],
  ["layers"],
  ["thickness"],
  ["copper_external", "external_copper_thickness"],
  ["copper_internal", "inner_copper_thickness"],
  ["stackup_method", "stackup", "stackup_type"],
  ["silkscreen_color", "silkscreen_ink_color", "silkscreen"],
  ["color_top", "solder_color_top"],
  ["color_bottom", "color_bot", "solder_color_bot"],
  ["soldermask_position", "position"],
  ["legend"],
  ["date_position"],
  ["pcb_supplier", "supplier"],
  ["ipc_class", "ipc"],
];

const PAYLOAD_PASSTHROUGH_KEYS = new Set(["quote_mode", "manual_note", "manual_reason"]);
const STRUCTURAL_AUTO_FETCH_KEYS = new Set(["family", "material", "layers"]);
const STRUCTURAL_AUTO_FETCH_TIMER_KEY = "__structural_auto_fetch__";
// ----------------------------
const StepPcbOrder = forwardRef(function StepPcbOrder(
  { projectId, disabled, initialOrderState, allowDraftRestore, selected, setSelected, onPrev, onContinue, onBusyChange },
  ref
) {
  const [updating, setUpdating] = useState(false);
  const [isAutoFetching, setIsAutoFetching] = useState(false);

  const [choices, setChoices] = useState({});
  const [choicesBase, setChoicesBase] = useState({});
  const [diff, setDiff] = useState(null);
  const [check, setCheck] = useState(null);

  const [price, setPrice] = useState(null);
  const [priceStale, setPriceStale] = useState(false);
  const [saveBundle, setSaveBundle] = useState(null);

  const offers = useMemo(() => {
    const arr = price?.offers || price?.delivery_items || price?.items || [];
    return Array.isArray(arr) ? arr : [];
  }, [price]);

  const [selectedOfferId, setSelectedOfferId] = useState("");

  useEffect(() => {
    if (!offers.length) {
      setSelectedOfferId("");
      return;
    }
    setSelectedOfferId(String(offers[offers.length - 1]?.id ?? ""));
  }, [offers]);

  const displayedOffer = useMemo(() => {
    if (!offers.length) return null;
    const idStr = String(selectedOfferId || "");
    const found = offers.find((o) => String(o?.id ?? "") === idStr);
    return found || offers[offers.length - 1] || null;
  }, [offers, selectedOfferId]);

  useEffect(() => {
  setSaveBundle((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      selected_offer_id: String(selectedOfferId || ""),
      displayed_offer: displayedOffer || null,
    };
  });
}, [selectedOfferId, displayedOffer]);

  const [pendingOverrides, setPendingOverrides] = useState([]);

  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [manualModalCtx, setManualModalCtx] = useState(null);
  const [manualNoteDraft, setManualNoteDraft] = useState("");

  const reqTokenRef = useRef(0);
  const touchedRef = useRef(new Set());
  const selectedRef = useRef(selected || {});
  const DRAFT_KEY = useMemo(() => `pcb_order_draft:${projectId}`, [projectId]);

  const debounceTimersRef = useRef(new Map());
  function clearDebounces() {
    const m = debounceTimersRef.current;
    for (const t of m.values()) clearTimeout(t);
    m.clear();
  }

  useEffect(() => {
    clearDebounces();
    touchedRef.current = new Set();

    setChoices({});
    setChoicesBase({});
    setDiff(null);
    setCheck(null);
    setPrice(initialOrderState?.price || null);
    setPriceStale(false);
    setPendingOverrides([]);
    setManualModalOpen(false);
    setManualModalCtx(null);
    setManualNoteDraft("");
    setUpdating(false);
    setIsAutoFetching(false);
    setSaveBundle(initialOrderState?.saveBundle || null);
    setSelectedOfferId(String(initialOrderState?.selectedOfferId || ""));


    if (allowDraftRestore == null) {
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const draftTs = Number(parsed?.ts || 0) || 0;
        const initialTs = Number(initialOrderState?.saveBundle?.ts || initialOrderState?.ts || 0) || 0;
        const shouldUseDraft = allowDraftRestore || draftTs > initialTs;
        if (!shouldUseDraft) return;

        const restoredSelected = parsed?.selected || null;
        const restoredPrice = parsed?.price && typeof parsed.price === "object" ? parsed.price : null;
        const restoredSaveBundle = parsed?.saveBundle && typeof parsed.saveBundle === "object" ? parsed.saveBundle : null;
        const restoredSelectedOfferId = String(parsed?.selectedOfferId || "");

        if (restoredSelected && typeof restoredSelected === "object") {
          setSelected(sanitizeSelectedState(restoredSelected));
        }
        setPrice(restoredPrice);
        setPriceStale(false);
        setSaveBundle(restoredSaveBundle);
        setSelectedOfferId(restoredSelectedOfferId);
        touchedRef.current = new Set();
      }
    } catch (e) {}
  }, [projectId, initialOrderState, allowDraftRestore]);

  useEffect(() => () => {
    clearDebounces();
  }, []);

  useEffect(() => {
    if (allowDraftRestore == null) return;
    try {
      const data = {
        selected: selected || {},
        price: price || null,
        saveBundle: saveBundle || null,
        selectedOfferId: String(selectedOfferId || ""),
        ts: Date.now(),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
    } catch (e) {}
  }, [selected, price, saveBundle, selectedOfferId, DRAFT_KEY, allowDraftRestore]);

  useEffect(() => {
    selectedRef.current = selected || {};
  }, [selected]);


  const sizeX = selected?.size_x ?? "";
  const sizeY = selected?.size_y ?? "";
  const qty = selected?.pcb_quantity ?? "1";

  function getUiValueForKey(key) {
    if (selected && Object.prototype.hasOwnProperty.call(selected, key)) {
      return selected[key] == null ? "" : String(selected[key]);
    }
    const direct = selected?.[key];
    if (direct !== undefined && direct !== null && direct !== "") return String(direct);

    const group = getFieldVariants(key);
    if (group) {
      for (const variant of group) {
        const v = selected?.[variant];
        if (v !== undefined && v !== null && v !== "") return String(v);
      }
      const hasExplicitEmpty = group.some((variant) => selected && Object.prototype.hasOwnProperty.call(selected, variant));
      if (hasExplicitEmpty) return "";
    }
    return FIELD_DEFAULTS[key]?.value ?? "";
  }

  function buildFallbackOptionForKey(key, rawValue) {
    const value = rawValue === undefined || rawValue === null ? "" : String(rawValue);
    if (!value) return null;

    const group = getFieldVariants(key);
    const defaultVariant = group.find((variant) => FIELD_DEFAULTS[variant]?.value === value);
    if (defaultVariant) {
      const opt = FIELD_DEFAULTS[defaultVariant];
      return { value, label: opt.label, status: "kept" };
    }

    const parsed = extractOptionSortNumber(key, { value, label: value });
    if (parsed !== null) return { value, label: formatNumericOptionLabel(key, parsed, value), status: "kept" };
    if (parsed !== null) {
      if (key === "layers") return { value, label: String(parsed), status: "kept" };
      if (key === "thickness") return { value, label: `${parsed} mm`, status: "kept" };
      return { value, label: `${parsed} µm`, status: "kept" };
    }

    return { value, label: value, status: "kept" };
  }


  const missingKeysSet = useMemo(() => new Set(check?.missing_keys || []), [check]);
  const diffAddedFieldsSet = useMemo(() => new Set(diff?.added_fields || []), [diff]);
  const diffRemovedFieldsSet = useMemo(() => new Set(diff?.removed_fields || []), [diff]);
  const diffChangedKeySet = useMemo(() => new Set(diff?.changed_keys || []), [diff]);

  const pendingMap = useMemo(() => {
    const m = new Map();
    (pendingOverrides || []).forEach((x) => x?.key && m.set(x.key, x));
    return m;
  }, [pendingOverrides]);


  function setSelectedKey(k, v) {
    touchedRef.current.add(k);

    setSelected((prev) => {
      const next = { ...(prev || {}) };
      if (v === "" || v === null || v === undefined) delete next[k];
      else next[k] = v;
      return next;
    });
  }

  function scheduleStructuralAutoFetch(nextSelected) {
    const timers = debounceTimersRef.current;
    const existingTimer = timers.get(STRUCTURAL_AUTO_FETCH_TIMER_KEY);
    if (existingTimer) clearTimeout(existingTimer);

    setIsAutoFetching(true);
    selectedRef.current = nextSelected;

    const timer = setTimeout(() => {
      timers.delete(STRUCTURAL_AUTO_FETCH_TIMER_KEY);
      fetchOptionsAndPrice(buildSelectedForPayload(selectedRef.current));
    }, 250);

    timers.set(STRUCTURAL_AUTO_FETCH_TIMER_KEY, timer);
  }

  function onSelectChange(key, value) {
    const variants = getFieldVariants(key);
    variants.forEach((variant) => touchedRef.current.add(variant));
    setPendingOverrides((prev) => (prev || []).filter((x) => x?.key !== key));

    const v = isNA(value) ? "" : value;
    const nextSelected = { ...(selected || {}) };
    variants.forEach((variant) => {
      delete nextSelected[variant];
    });
    nextSelected[key] = v;

    setSelected(nextSelected);

    if (v === "SPECIAL" && !COPPER_UM_KEYS.has(key)) {
      openManualModalForKey(key, v, "SPECIAL");
      return;
    }
    if (price) setPriceStale(true);
    if (STRUCTURAL_AUTO_FETCH_KEYS.has(key) && v && v !== "SPECIAL") {
      scheduleStructuralAutoFetch(nextSelected);
    }
  }

  const buildSelectedForPayload = useCallback((baseSelected, { includeDefaults = true } = {}) => {
    const PAYLOAD_DEFAULTS = {
      family:              "RIGID",
      material:            "S1000H",
      surface_finish:      "CHEM.GOLD",
      layers:              "4",
      thickness:           "S1000H_1.6mm",
      copper_external:     "MUL_35um",
      copper_internal:     "17um",
      stackup_method:      "SAFE",
      silkscreen_color:    "WHITE",
      color_top:           "GREEN",
      color_bottom:        "GREEN",
      soldermask_position: "TOP.BOT",
      legend:              "WITHOUT",
      date_position:       "WITHOUT",
      pcb_supplier:        "WITHOUT",
      ipc_class:           "CLASS_2",
    };
    const src = baseSelected && typeof baseSelected === "object" ? baseSelected : {};
    const out = {};
    const groupedKeys = new Set(SHOWN_FIELDS_ORDER.flat());

    if (src.size_x) out.size_x = String(src.size_x);
    if (src.size_y) out.size_y = String(src.size_y);
    if (src.pcb_quantity) out.pcb_quantity = String(src.pcb_quantity);

    const parseNum = (s) => {
      s = String(s || "").toLowerCase().replace(",", ".");
      if (s.includes("_")) s = s.split("_").pop();
      s = s.replace(/[a-z]+$/, "").trim();
      const n = parseFloat(s);
      return isNaN(n) ? null : n;
    };

    const hasOwnKey = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);
    const hasOwnMeaningfulValue = (obj, key) => hasOwnKey(obj, key) && hasMeaningfulValue(obj?.[key]);
    const getScopedFieldOpts = (key, fieldOpts) => {
      const opts = Array.isArray(fieldOpts) ? fieldOpts : [];
      if (key !== "thickness") return opts;

      const materialValue = String(out.material ?? src.material ?? PAYLOAD_DEFAULTS.material ?? "").trim().toUpperCase();
      if (!materialValue) return opts;

      const compatibleOpts = opts.filter((o) =>
        String(toOptValue(o) ?? "").trim().toUpperCase().startsWith(`${materialValue}_`)
      );

      return compatibleOpts.length ? compatibleOpts : opts;
    };

    const resolveChoiceValue = (key, rawValue) => {
      if (!hasMeaningfulValue(rawValue)) return "";
      const fieldOpts = getScopedFieldOpts(key, (choices[key]?.options) || []);
      if (!fieldOpts.length) return String(rawValue);

      const rawStr = String(rawValue);
      const exactMatch = fieldOpts.find((o) => String(toOptValue(o) ?? "") === rawStr);
      if (exactMatch) return String(toOptValue(exactMatch));

      const labelMatch = fieldOpts.find((o) => normStr(toOptLabel(o)) === normStr(rawStr));
      if (labelMatch) return String(toOptValue(labelMatch));

      const numericLabelMatch = fieldOpts.find((o) => {
        const optValue = String(toOptValue(o) ?? "");
        const optLabel = toOptLabel(o);
        const parsed = extractOptionSortNumber(key, { value: optValue, label: optLabel });
        if (parsed === null) return false;
        return normStr(formatNumericOptionLabel(key, parsed, optLabel || optValue)) === normStr(rawStr);
      });
      if (numericLabelMatch) return String(toOptValue(numericLabelMatch));

      const rawNum = parseNum(rawStr);
      if (rawNum !== null) {
        const numericMatch = fieldOpts.find((o) => {
          const optNum = parseNum(toOptValue(o)) ?? parseNum(toOptLabel(o));
          return optNum !== null && Math.abs(optNum - rawNum) < 0.001;
        });
        if (numericMatch) return String(toOptValue(numericMatch));
      }

      return "";
    };

    for (const variants of SHOWN_FIELDS_ORDER) {
      const payloadKey =
        variants.find((variant) => choices?.[variant]) ||
        variants[0];

      let rawValue = "";
      if (hasOwnMeaningfulValue(src, payloadKey)) {
        rawValue = src[payloadKey];
      } else {
        const selectedVariant = variants.find((variant) => hasOwnMeaningfulValue(src, variant));
        if (selectedVariant) rawValue = src[selectedVariant];
      }

      const hasExplicitEmptySelection = variants.some(
        (variant) => hasOwnKey(src, variant) && !hasMeaningfulValue(src[variant])
      );
      const fieldOpts = getScopedFieldOpts(payloadKey, (choices[payloadKey]?.options) || []);
      if (!hasMeaningfulValue(rawValue) && includeDefaults && !hasExplicitEmptySelection) {
        const defaultOpt = findDefaultOpt(fieldOpts);
        if (defaultOpt) rawValue = toOptValue(defaultOpt);
        else rawValue = PAYLOAD_DEFAULTS[payloadKey] ?? FIELD_DEFAULTS[payloadKey]?.value ?? "";
      }

      const resolvedValue = resolveChoiceValue(payloadKey, rawValue);
      if (hasMeaningfulValue(resolvedValue)) {
        out[payloadKey] = String(resolvedValue);
        continue;
      }

      if (hasMeaningfulValue(rawValue)) {
        out[payloadKey] = String(rawValue);
      }
    }

    for (const [k, val] of Object.entries(src)) {
      if (k === "size_x" || k === "size_y" || k === "pcb_quantity") continue;
      if (groupedKeys.has(k)) continue;
      if (!PAYLOAD_PASSTHROUGH_KEYS.has(k)) continue;
      if (val === "" || val === null || val === undefined) continue;
      const fieldOpts = (choices[k]?.options) || [];
      if (fieldOpts.length > 0) {
        const validVals = new Set(fieldOpts.map((o) => String(toOptValue(o) ?? "")));
        if (!validVals.has(String(val))) {
          const vNum = parseNum(val);
          const match = vNum !== null
            ? fieldOpts.find((o) => {
                const oNum = parseNum(toOptValue(o)) ?? parseNum(toOptLabel(o));
                return oNum !== null && Math.abs(oNum - vNum) < 0.001;
              })
            : null;
          if (match) out[k] = String(toOptValue(match));
          continue;
        }
      }
      out[k] = String(val);
    }

    return out;
  }, [choices]);

  function validateEssentials(payloadSelected) {
    const sx = toNum(payloadSelected.size_x);
    const sy = toNum(payloadSelected.size_y);
    const q = toNum(payloadSelected.pcb_quantity);

    const minSize = getSizeMin(selected);

    if (sx === null || sy === null || q === null) return false;
    if (sx <= 0 || sy <= 0 || q <= 0) return false;
    if (sx < minSize || sy < minSize) return false;
    return true;
  }

  function validateEssentialsWithToast(payloadSelected) {
    const sx = toNum(payloadSelected.size_x);
    const sy = toNum(payloadSelected.size_y);
    const q = toNum(payloadSelected.pcb_quantity);

    const minSize = getSizeMin(selected);

    if (sx === null || sy === null || q === null) {
      errorMsg("Veuillez saisir Size X et Size Y avant de mettre à jour.");
      return false;
    }
    if (sx <= 0 || sy <= 0 || q <= 0) {
      errorMsg("Veuillez saisir Size X et Size Y (valeurs > 0) avant de mettre à jour.");
      return false;
    }
    if (sx < minSize || sy < minSize) {
      errorMsg(`Size X/Y doit être >= ${minSize} mm pour cette family (règles Safe-PCB).`);
      return false;
    }
    return true;
  }

  function hasNullLabel(o) {
    return Object.prototype.hasOwnProperty.call(o || {}, "label") && o.label == null;
  }

  function buildMergedOptionsForKey(key) {
    const baseDef = choicesBase?.[key];
    const filtDef = choices?.[key];
    const currentValue = getUiValueForKey(key);

    const baseOpts = Array.isArray(baseDef?.options) ? baseDef.options : [];
    const filtOpts = Array.isArray(filtDef?.options) ? filtDef.options : [];
    if (baseOpts.length === 0 && filtOpts.length === 0) {
      const fallback = buildFallbackOptionForKey(key, currentValue);
      if (fallback) return [fallback];
      const d = FIELD_DEFAULTS[key];
      if (d) return [{ value: d.value, label: d.label, status: "kept" }];
      return [];
    }

    const remapSentinelLabel = (v, rawLabel) => {
      if (v === "NA") return null; // hide NA (means 0 on backend)
      if (v === "SPECIAL") {
        if (COPPER_UM_KEYS.has(key)) return null;
        if (key === "copper_external" || key === "copper_internal") return null; // non supporté fabricant
        return "SPECIAL";
      }
      const parsed = extractOptionSortNumber(key, { value: v, label: rawLabel || v });
      if (parsed !== null) return formatNumericOptionLabel(key, parsed, rawLabel || v);
      return rawLabel || v;
    };

    const baseMap = new Map();
    baseOpts.forEach((o) => {
      if (hasNullLabel(o)) return;
      const v = String(toOptValue(o) ?? "");
      if (!v) return;
      const label = remapSentinelLabel(v, toOptLabel(o));
      if (label === null) return; // filtered out
      baseMap.set(v, { value: v, label });
    });

    const filtMap = new Map();
    filtOpts.forEach((o) => {
      if (hasNullLabel(o)) return;
      const v = String(toOptValue(o) ?? "");
      if (!v) return;
      const label = remapSentinelLabel(v, toOptLabel(o));
      if (label === null) return; // filtered out
      filtMap.set(v, { value: v, label });
    });

    const merged = [];

    for (const [v, obj] of filtMap.entries()) {
      merged.push({ value: v, label: obj.label, status: baseMap.has(v) ? "kept" : "added" });
    }
    for (const [v, obj] of baseMap.entries()) {
      if (filtMap.has(v)) continue;
      merged.push({ value: v, label: obj.label, status: "removed" });
    }

    if (currentValue && !merged.some((o) => String(o?.value ?? "") === String(currentValue))) {
      const fallback = buildFallbackOptionForKey(key, currentValue);
      if (fallback) merged.push(fallback);
    }

    const rank = (s) => (s === "added" ? 0 : s === "kept" ? 1 : 2);
    merged.sort((a, b) => {
      const ra = rank(a.status);
      const rb = rank(b.status);
      if (ra !== rb) return ra - rb;
      const an = extractOptionSortNumber(key, a);
      const bn = extractOptionSortNumber(key, b);
      if (an !== null || bn !== null) {
        if (an === null) return 1;
        if (bn === null) return -1;
        if (an !== bn) return an - bn;
      }
      return String(a.label).localeCompare(String(b.label));
    });

    return makeLabelsUnique(merged);
  }

  function getChangedCountForKey(key) {
    const arr = diff?.changed_fields || [];
    const found = arr.find((x) => x?.key === key);
    if (!found) return null;
    const before = found.before;
    const after = found.after;
    if (typeof before === "number" && typeof after === "number") return `${before} à ${after}`;
    if (typeof after === "number") return String(after);
    return null;
  }

  function buildMetaBadgesForKey(key) {
    const missing = missingKeysSet.has(key);
    const filtered = diffChangedKeySet.has(key);
    const added = diffAddedFieldsSet.has(key);
    const removed = diffRemovedFieldsSet.has(key);
    const pending = pendingMap.has(key);
    const changedCount = filtered ? getChangedCountForKey(key) : null;
    return { missing, filtered, added, removed, pending, changedCount };
  }

  function computePendingOverrides(resOverridden) {
    const list = Array.isArray(resOverridden) ? resOverridden : [];
    const nextPending = [];

    for (const o of list) {
      const key = o?.key;
      if (!key) continue;

      const requested = o?.requested;
      const applied = o?.applied;

      if (isFormattingOnly(requested, applied)) continue;
      if (!touchedRef.current.has(key)) continue;

      const appliedStr = normStr(applied);
      const blocking = isEmptyOrNA(appliedStr);

      let msg = "";
      if (blocking) {
        msg = "Choix incompatible (Safe-PCB renvoie NA). Veuillez sélectionner une autre option ou passer par un chiffrage manuel.";
      } else if (isWITHOUT(appliedStr)) {
        msg = "Safe-PCB ne permet pas ce choix avec la configuration actuelle. Proposition: WITHOUT (sans option).";
      } else {
        msg = `Choix non accepté (Safe-PCB propose ${appliedStr}).`;
      }

      nextPending.push({
        key,
        requested,
        applied,
        blocking,
        suggestedValue: blocking ? "" : appliedStr,
        message: msg,
      });
    }

    setPendingOverrides((prev) => {
      const m = new Map((prev || []).map((x) => [x.key, x]));
      for (const x of nextPending) m.set(x.key, x);
      return Array.from(m.values());
    });
  }

  async function fetchOptionsAndPrice(payloadSelected, { showToasts = false } = {}) {
    const token = ++reqTokenRef.current;

    if (showToasts) setUpdating(true);
    else setIsAutoFetching(true);

    try {
      if (!payloadSelected.pcb_quantity) payloadSelected.pcb_quantity = "1";

      const ok = showToasts ? validateEssentialsWithToast(payloadSelected) : validateEssentials(payloadSelected);
      if (!ok) return;

      const payload = {
        pcb_spec: {
          client_pcb_mode: "order",
          pcb_quantity: payloadSelected.pcb_quantity,
          selected_choices: payloadSelected,
        },
      };

      const res = await postJson(`/user/projects/${projectId}/pcb-options/`, payload);
      if (token !== reqTokenRef.current) return;

      if (res?.error === "missing_required_fields" && showToasts) {
        const fields = (res.fields || []).join(", ");
        errorMsg(`Champs obligatoires manquants : ${fields}. Veuillez les sélectionner avant de demander un prix.`);
      }

      setChoices(res?.choices || {});
      setChoicesBase(res?.choices_base || res?.choicesBase || {});
      setDiff(res?.diff || null);
      setCheck(res?.check || null);
      setPrice(res?.price || null);
      setPriceStale(false);
      computePendingOverrides(res?.overridden || []);

      const newChoices = res?.choices || {};
      const backendSelected =
        (res?.selected_clean && typeof res.selected_clean === "object" && res.selected_clean) ||
        (res?.applied && typeof res.applied === "object" && res.applied) ||
        (res?.selected_sent && typeof res.selected_sent === "object" && res.selected_sent) ||
        null;

      const parseNum = (s) => {
        s = String(s || "").toLowerCase().replace(",", ".");
        if (s.includes("_")) s = s.split("_").pop();
        s = s.replace(/[a-z]+$/, "").trim();
        const n = parseFloat(s);
        return isNaN(n) ? null : n;
      };
      const getScopedNewChoiceOpts = (key, fieldOpts, selectedSnapshot) => {
        const opts = Array.isArray(fieldOpts) ? fieldOpts : [];
        if (key !== "thickness") return opts;

        const materialValue = String(selectedSnapshot?.material ?? "").trim().toUpperCase();
        if (!materialValue) return opts;

        const compatibleOpts = opts.filter((o) =>
          String(toOptValue(o) ?? "").trim().toUpperCase().startsWith(`${materialValue}_`)
        );

        return compatibleOpts.length ? compatibleOpts : opts;
      };

      setSelected((prev) => {
        const next = { ...(prev || {}) };
        let changed = false;

        if (backendSelected) {
          const setCanonicalGroupValue = (variants) => {
            const touched = variants.some((variant) => touchedRef.current.has(variant));
            if (touched) return;

            const serverVariant = variants.find((variant) => hasMeaningfulValue(backendSelected?.[variant]));
            if (!serverVariant) return;

            const canonicalKey = variants[0];
            const serverValue = String(backendSelected[serverVariant]);
            const alreadyCanonical =
              String(next?.[canonicalKey] ?? "") === serverValue &&
              variants.every((variant) =>
                variant === canonicalKey ? true : !hasMeaningfulValue(next?.[variant])
              );

            if (alreadyCanonical) return;

            variants.forEach((variant) => {
              if (Object.prototype.hasOwnProperty.call(next, variant)) delete next[variant];
            });
            next[canonicalKey] = serverValue;
            changed = true;
          };

          SHOWN_FIELDS_ORDER.forEach(setCanonicalGroupValue);

          ["size_x", "size_y", "pcb_quantity"].forEach((key) => {
            if (touchedRef.current.has(key)) return;
            if (!hasMeaningfulValue(backendSelected?.[key])) return;
            const serverValue = String(backendSelected[key]);
            if (String(next?.[key] ?? "") === serverValue) return;
            next[key] = serverValue;
            changed = true;
          });
        }
        for (const [k, v] of Object.entries(next)) {
          if (touchedRef.current.has(k)) continue; // ne jamais écraser un choix utilisateur
          const fieldOpts = getScopedNewChoiceOpts(k, (newChoices[k]?.options) || [], next);
          if (!fieldOpts.length) continue;
          const validVals = new Set(fieldOpts.map((o) => String(toOptValue(o) ?? "")));
          if (validVals.has(String(v))) continue; // déjà valide
          const vNum = parseNum(v);
          if (vNum !== null) {
            const numMatch = fieldOpts.find((o) => {
              const oNum = parseNum(toOptValue(o)) ?? parseNum(toOptLabel(o));
              return oNum !== null && Math.abs(oNum - vNum) < 0.001;
            });
            if (numMatch) { next[k] = String(toOptValue(numMatch)); changed = true; continue; }
          }
          const def = findDefaultOpt(fieldOpts);
          if (def) { next[k] = String(toOptValue(def)); changed = true; }
        }
        for (const variants of SHOWN_FIELDS_ORDER) {
          const apiKey = variants.find((v) => newChoices[v]);
          if (!apiKey) continue;
          if (touchedRef.current.has(apiKey)) continue;
          const hasValue = variants.some((v) => next[v] !== undefined && next[v] !== null && next[v] !== "");
          if (hasValue) continue;
          const fieldOpts = getScopedNewChoiceOpts(apiKey, (newChoices[apiKey]?.options) || [], next);
          const def = findDefaultOpt(fieldOpts);
          if (def) { next[apiKey] = String(toOptValue(def)); changed = true; }
        }

        return changed ? next : prev;
      });
      const appliedSelected =
        res?.applied ||
        res?.selected_clean ||
        res?.selected_sent ||
        payloadSelected ||
        {};
      const normalizedAppliedSelected = buildSelectedForPayload(appliedSelected, { includeDefaults: false });
      setSaveBundle({
        ts: Date.now(),
        project_id: projectId,
        selected_choices: normalizedAppliedSelected,
        selected_clean: res?.selected_clean || null,
        selected_sent: res?.selected_sent || null,
        price: res?.price || null,
        offers: res?.offers || res?.price?.offers || res?.price?.items || [],
        selected_offer_id: String(selectedOfferId || ""),
        displayed_offer: displayedOffer || null,
        check: res?.check || null,
        diff: res?.diff || null,
        overridden: res?.overridden || [],
        invalid_selected: res?.invalid_selected || [],
        errors: res?.errors || [],
        status: res?.status || null,
      });
    } catch (e) {
      console.error(e);
      if (showToasts) errorMsg(e);
    } finally {
      if (token === reqTokenRef.current) {
        setIsAutoFetching(false);
        setUpdating(false);
      }
    }
  }

  async function refreshOptionsManual() {
    const curSelected = selected || {};
    const payloadSelected = buildSelectedForPayload(curSelected);
    await fetchOptionsAndPrice(payloadSelected, { showToasts: true });
  }


  function resetDraft({ keepEssentials = false } = {}) {
    clearDebounces();
    reqTokenRef.current += 1;

    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {}

    touchedRef.current = new Set();

    setChoices({});
    setChoicesBase({});
    setDiff(null);
    setCheck(null);
    setPrice(null);
    setPriceStale(false);
    setPendingOverrides([]);
    setManualModalOpen(false);
    setManualModalCtx(null);
    setManualNoteDraft("");
    setUpdating(false);
    setIsAutoFetching(false);
    setSaveBundle(null);

    setSelected((prev) => {
      if (!keepEssentials) return {};
      const p = prev || {};
      const next = {};
      if (p.family) next.family = p.family;
      if (p.size_x) next.size_x = p.size_x;
      if (p.size_y) next.size_y = p.size_y;
      if (p.pcb_quantity) next.pcb_quantity = p.pcb_quantity;
      return next;
    });
  }

  function acceptSuggestedValue(key, applied) {
    setPendingOverrides((prev) => (prev || []).filter((x) => x?.key !== key));
    setSelected((prev) => {
      const next = { ...(prev || {}) };
      if (isEmptyOrNA(applied) || normStr(applied) === "") delete next[key];
      else next[key] = String(applied);
      return next;
    });
  }

  function openManualModalForKey(key, requested, applied) {
    setManualModalCtx({ key, requested, applied });
    setManualNoteDraft("");
    setManualModalOpen(true);
  }

  function confirmManualQuote() {
    const note = normStr(manualNoteDraft);
    if (!note) {
      errorMsg("Veuillez saisir une note pour le chiffrage manuel.");
      return;
    }

    const ctx = manualModalCtx;
    const reason =
      ctx?.key
        ? `Override sur ${ctx.key}: requested=${String(ctx.requested)} applied=${String(ctx.applied)}`
        : "Demande chiffrage manuel";

    setSelected((prev) => ({
      ...(prev || {}),
      quote_mode: "MANUAL",
      manual_note: note,
      manual_reason: reason,
    }));

    if (ctx?.key) {
      setPendingOverrides((prev) => (prev || []).filter((x) => x?.key !== ctx.key));
    }

    setManualModalOpen(false);
    setManualModalCtx(null);
    setManualNoteDraft("");
  }


  const summary = useMemo(() => {
    const changed = diff?.changed_keys?.length || 0;
    const addF = diff?.added_fields?.length || 0;
    const remF = diff?.removed_fields?.length || 0;
    const pending = (pendingOverrides || []).length || 0;
    return { changed, addF, remF, pending };
  }, [diff, pendingOverrides]);

  const isManual = (selected?.quote_mode || "") === "MANUAL";
  const hasManualNote = !!normStr(selected?.manual_note);
  const canContinue = (pendingOverrides?.length || 0) === 0 && (!isManual || hasManualNote) && !priceStale;

  const minSize = getSizeMin(selected);

  function resolveVisibleFieldKeys(orderedGroups) {
    const out = [];
    for (const variants of orderedGroups) {
      const apiKey = variants.find((v) => choices?.[v] || choicesBase?.[v]);
      const key = apiKey || variants[0];
      const def = choices?.[key] || choicesBase?.[key];
      if (def && !isFieldAllowedByFamily(def, selected)) continue;
      out.push(key);
    }
    return out;
  }

  const dynamicKeys = resolveVisibleFieldKeys(SHOWN_FIELDS_ORDER);
  const dynamicLeft = dynamicKeys;
  const dynamicRight = [];

  const disableFields = !!disabled || updating || isAutoFetching;
  const disableButtons = !!disabled || updating || isAutoFetching;

  useEffect(() => {
    onBusyChange?.(updating || isAutoFetching);
    return () => {
      onBusyChange?.(false);
    };
  }, [isAutoFetching, onBusyChange, updating]);

  useImperativeHandle(ref, () => ({
  canContinue: () => canContinue && !!price && !!displayedOffer && !priceStale && !updating && !isAutoFetching,
  getOrderConfigPayload: () => {
    const applied = buildSelectedForPayload(saveBundle?.selected_choices || {}, { includeDefaults: false });

    const offersArr = offers || [];
    const selectedId = String(selectedOfferId || "");
    const offer =
      offersArr.find((o) => String(o?.id ?? "") === selectedId) ||
      offersArr[offersArr.length - 1] ||
      null;

    return {
      applied_selected: applied,
      offer_selected_id: selectedId || (offer ? String(offer.id ?? "") : ""),
      offer_selected: offer,
      price_snapshot: price || null,
      safe_pcb_bundle: saveBundle || null, // optionnel
    };
  },
}), [buildSelectedForPayload, canContinue, price, priceStale, displayedOffer, offers, selectedOfferId, saveBundle, updating, isAutoFetching]);

  return (
    <div style={{ width: "100%" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div
        className="info-box"
        style={{
          marginBottom: 14,
          borderRadius: 12,
          padding: 12,
          border: "1px solid rgba(0,0,0,0.12)",
        }}
      >
        {check?.ok ? <div>? {check.message || "OK"}</div> : <div> {check?.message || "Paramètres à compléter"}</div>}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, fontSize: 13, alignItems: "center" }}>
          <span style={{ color: "#f08c00", fontWeight: 800 }}> FILTRÉE : {summary.changed}</span>
          {/* <span style={{ color: "#2f9e44", fontWeight: 800 }}> Champs ajoutés : {summary.addF}</span>
          <span style={{ color: "#d64545", fontWeight: 800 }}> Champs supprimés : {summary.remF}</span> */}
          <span style={{ color: "#d64545", fontWeight: 800 }}> Action requise : {summary.pending}</span>
          {isManual ? <span style={{ color: "#7a4bd1", fontWeight: 900 }}> MODE : CHIFFRAGE MANUEL</span> : null}

          {isAutoFetching && !updating ? (
            <span
              style={{
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                padding: "2px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.15)",
                background: "rgba(0,0,0,0.03)",
                fontWeight: 900,
              }}
            >
              <Spinner size={12} /> Chargement auto… Patientez...
            </span>
          ) : null}
        </div>

        {isManual && !hasManualNote ? (
          <div style={{ marginTop: 10, color: "#d64545", fontWeight: 900 }}>
            Mode chiffrage manuel actif: une note est obligatoire avant de continuer.
          </div>
        ) : null}

        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="btn primary" onClick={refreshOptionsManual} disabled={disableButtons}>
            {updating ? "Mise à jour..." : "Mettre à jour"}
          </button>

          <button
            type="button"
            className="btn"
            disabled={disableButtons}
            onClick={() => {
              if (window.confirm("Réinitialiser le brouillon et repartir à zéro ?")) {
                resetDraft({ keepEssentials: false });
              }
            }}
            style={{ background: "#fff", color: "#111", border: "1px solid rgba(0,0,0,0.25)" }}
          >
             Réinitialiser le brouillon
          </button>
        </div>
      </div>

      <div
        style={{
          display: "block",
        }}
      >
        {/* LEFT PAGE */}
        <div>
          <NumericStepper
            label={`Size X (mm) (min ${minSize})`}
            value={sizeX}
            onChange={(v) => setSelectedKey("size_x", v)}
            unit="mm"
            step={1}
            bigStep={10}
            min={minSize}
            disabled={disableFields}
            missing={missingKeysSet.has("size_x")}
            presets={[50, 100, 150, 200, 300]}
          />

          <NumericStepper
            label={`Size Y (mm) (min ${minSize})`}
            value={sizeY}
            onChange={(v) => setSelectedKey("size_y", v)}
            unit="mm"
            step={1}
            bigStep={10}
            min={minSize}
            disabled={disableFields}
            missing={missingKeysSet.has("size_y")}
            presets={[50, 80, 100, 150, 200]}
          />

          <div style={{ marginTop: 14 }}>
            {dynamicLeft.map((key) => {
              const def = choices?.[key] || choicesBase?.[key];
              const label = FIELD_LABELS[key] || apiLabelFromDef(def, key);
              const mergedOptions = buildMergedOptionsForKey(key);

              const curVal = getUiValueForKey(key);
              const metaBadges = buildMetaBadgesForKey(key);
              const pend = pendingMap.get(key);

              const pendingBlock = pend ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid rgba(214,69,69,0.35)",
                    background: "rgba(214,69,69,0.06)",
                  }}
                >
                  <div style={{ color: "#d64545", fontWeight: 900, marginBottom: 6 }}>{pend.message}</div>

                  <div style={{ fontSize: 13, marginBottom: 10 }}>
                    Demandé : <b>{String(pend.requested)}</b> → Proposé : <b>{String(pend.applied)}</b>
                  </div>

                  {pend.blocking ? (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => openManualModalForKey(key, pend.requested, pend.applied)}
                        disabled={disableButtons}
                        style={{ border: "1px solid rgba(122,75,209,0.55)" }}
                      >
                        Chiffrage manuel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => acceptSuggestedValue(key, pend.suggestedValue)}
                        disabled={disableButtons}
                      >
                        ✅ Accepter {String(pend.suggestedValue)}
                      </button>

                      <button
                        type="button"
                        className="btn"
                        onClick={() => openManualModalForKey(key, pend.requested, pend.applied)}
                        disabled={disableButtons}
                        style={{ border: "1px solid rgba(122,75,209,0.55)" }}
                      >
                        Chiffrage manuel
                      </button>
                    </div>
                  )}
                </div>
              ) : null;

              return (
                <SelectField
                  key={`L:${key}`}
                  label={label}
                  value={curVal}
                  mergedOptions={mergedOptions}
                  disabled={disableFields}
                  missing={metaBadges.missing}
                  metaBadges={metaBadges}
                  onChange={(v) => onSelectChange(key, v)}
                  pendingBlock={pendingBlock}
                />
              );
            })}
          </div>
        </div>

        {/* RIGHT PAGE */}
        <div>
          {dynamicRight.map((key) => {
            const def = choices?.[key] || choicesBase?.[key];
            const label = FIELD_LABELS[key] || apiLabelFromDef(def, key);
            const mergedOptions = buildMergedOptionsForKey(key);

            const curVal = getUiValueForKey(key);
            const metaBadges = buildMetaBadgesForKey(key);
            const pend = pendingMap.get(key);

            const pendingBlock = pend ? (
              <div
                style={{
                  marginTop: 8,
                  padding: 10,
                  borderRadius: 10,
                  border: "1px solid rgba(214,69,69,0.35)",
                  background: "rgba(214,69,69,0.06)",
                }}
              >
                <div style={{ color: "#d64545", fontWeight: 900, marginBottom: 6 }}>{pend.message}</div>

                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  Demandé : <b>{String(pend.requested)}</b> → Proposé : <b>{String(pend.applied)}</b>
                </div>

                {pend.blocking ? (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => openManualModalForKey(key, pend.requested, pend.applied)}
                      disabled={disableButtons}
                      style={{ border: "1px solid rgba(122,75,209,0.55)" }}
                    >
                      Chiffrage manuel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => acceptSuggestedValue(key, pend.suggestedValue)}
                      disabled={disableButtons}
                    >
                      ✅ Accepter {String(pend.suggestedValue)}
                    </button>

                    <button
                      type="button"
                      className="btn"
                      onClick={() => openManualModalForKey(key, pend.requested, pend.applied)}
                      disabled={disableButtons}
                      style={{ border: "1px solid rgba(122,75,209,0.55)" }}
                    >
                      Chiffrage manuel
                    </button>
                  </div>
                )}
              </div>
            ) : null;

            return (
              <SelectField
                key={`R:${key}`}
                label={label}
                value={curVal}
                mergedOptions={mergedOptions}
                disabled={disableFields}
                missing={metaBadges.missing}
                metaBadges={metaBadges}
                onChange={(v) => onSelectChange(key, v)}
                pendingBlock={pendingBlock}
              />
            );
          })}
        </div>
      </div>

      {/* ? PRICE sticky footer + OFFERS + ACTIONS */}
<div
  style={{
    position: "sticky",
    bottom: 0,
    marginTop: 16,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "linear-gradient(180deg, rgba(18,20,28,0.96) 0%, rgba(11,13,19,0.98) 100%)",
    boxShadow: "0 -16px 40px rgba(0,0,0,0.32)",
    backdropFilter: "blur(12px)",
    color: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    zIndex: 20,
  }}
>
  {/* LEFT: PRICE */}
  <div style={{ minWidth: 340, flex: 1 }}>
    <div
      style={{
        fontSize: 12,
        opacity: 0.82,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      Prix PCB (Safe-PCB)

      {(updating || isAutoFetching) ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 900, opacity: 0.9 }}>
          <Spinner size={12} /> Calcul...
        </span>
      ) : null}
    </div>

    {displayedOffer ? (
      <>
        <div style={{ fontSize: 20, fontWeight: 950, marginTop: 2 }}>
          {Number(displayedOffer.total ?? 0).toFixed(3)}{" "}
          {displayedOffer.currency || price?.currency || "EUR"}
        </div>

        <div style={{ fontSize: 12, opacity: 0.88, marginTop: 6, lineHeight: 1.4 }}>
          <div>
            <b>Qté PCB</b> : {qty} -{" "}
            <b>Qté circuit</b> : {displayedOffer.circuit_qty ?? "-"}{" "}
            -{" "}
            <b>Qté panel</b> : {displayedOffer.panel_qty ?? "-"}
          </div>

          <div>
            <b>Production</b> : {displayedOffer.production_term || `${displayedOffer.production_days ?? "-"} jours`}{" "}
            -{" "}
            <b>Livraison</b> : {displayedOffer.shipment_term || `${displayedOffer.shipment_days ?? "-"} jours`}
          </div>

          <div>
            <b>Expédition</b>: {displayedOffer.expedition_due_date ?? "-"}{" "}
            -{" "}
            <b>Livraison</b>: {displayedOffer.delivery_due_date ?? "-"}
          </div>

          {displayedOffer.vat_rate != null || displayedOffer.total_vat != null ? (
            <div>
              <b>TVA</b> : {displayedOffer.vat_rate ?? "-"}%{" "}
              -{" "}
              <b>Montant TVA</b> : {displayedOffer.total_vat ?? "-"}
            </div>
          ) : null}
        </div>

        {Array.isArray(offers) && offers.length > 1 ? (
          <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.85 }}>Choisir une offre :</div>

            <select
              className="select"
              value={selectedOfferId}
              onChange={(e) => setSelectedOfferId(e.target.value)}
              disabled={disableButtons}
              style={{
                maxWidth: 460,
                color: "#f8fafc",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 10,
                padding: "8px 10px",
                fontWeight: 800,
              }}
            >
              {offers.map((o) => {
                const id = String(o?.id ?? "");
                const total = o?.total != null ? Number(o.total).toFixed(3) : "-";
                const curr = o?.currency || "€";
                const prod = o?.production_term || `${o?.production_days ?? "-"} jours`;
                const ship = o?.shipment_term || `${o?.shipment_days ?? "-"} jours`;
                const cqty = o?.circuit_qty ?? "-";
                const pqty = o?.panel_qty ?? "-";
                const del = o?.delivery_due_date ?? "-";

                return (
                  <option key={id} value={id}>
                    Offre {id} : {total}{curr} - Prod : {prod} - Expédition : {ship} - Circuit : {cqty} - Panel : {pqty} - Livraison : {del}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
      </>
    ) : (
      <div style={{ fontSize: 14, fontWeight: 900, opacity: 0.6, marginTop: 6 }}>
        Cliquez sur <b>Mettre à jour</b> pour calculer le prix.
      </div>
    )}

    {priceStale && price ? (
      <div style={{ marginTop: 8, color: "#f08c00", fontWeight: 900, fontSize: 13 }}>
        ⚠ Configuration modifiée: prix à recalculer avant de continuer.
      </div>
    ) : null}
  </div>

 
</div>

      <Modal
        open={manualModalOpen}
        title="Chiffrage manuel: Note obligatoire"
        onClose={() => {
          setManualModalOpen(false);
          setManualModalCtx(null);
          setManualNoteDraft("");
        }}
      >
        {manualModalCtx?.key ? (
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            <div>
              Champ : <b>{manualModalCtx.key}</b>
            </div>
            <div>
              Demandé : <b>{String(manualModalCtx.requested)}</b> → Appliqué : <b>{String(manualModalCtx.applied)}</b>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, marginBottom: 10, opacity: 0.9 }}>
            Vous êtes en mode chiffrage manuel. Merci d'expliquer la demande.
          </div>
        )}

        <textarea
          value={manualNoteDraft}
          onChange={(e) => setManualNoteDraft(e.target.value)}
          rows={5}
          placeholder="Expliquez la demande (ex: besoin Class 3, matériau spécial ...)"
          style={{
            width: "100%",
            borderRadius: 10,
            padding: 10,
            border: "1px solid rgba(0,0,0,0.2)",
            resize: "vertical",
          }}
          disabled={disableButtons}
        />

        {!normStr(manualNoteDraft) ? (
          <div style={{ marginTop: 8, color: "#d64545", fontWeight: 900 }}>Merci d'ajouter une note avant de confirmer.</div>
        ) : null}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setManualModalOpen(false);
              setManualModalCtx(null);
              setManualNoteDraft("");
            }}
            disabled={disableButtons}
            style={{ background: "#fff", color: "#111", border: "1px solid rgba(0,0,0,0.25)" }}
          >
            Annuler
          </button>
          <button type="button" className="btn primary" onClick={confirmManualQuote} disabled={disableButtons}>
            Confirmer
          </button>
        </div>
      </Modal>
    </div>
  );
});

export default StepPcbOrder;
